'use client';

import { FC, useCallback, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { SampleGridCore, SampleRowType } from '@/components/tables/SampleGridCore';
import { useSampleGridPersistScopeKey } from '@/hooks/useSampleGridPersistScopeKey';
import { SampleSearchPanel } from '@/components/tables/search/SampleSearchPanel';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import type { SampleSearchCriteria } from '@/api/ApiClient';
import { useSampleFilterStore, defaultFilterCriteria, type FilterSourceFlags } from '@/stores/sampleFilterStore';

/** Stable React `key` for the search panel: only changes when preset criteria change (not every dialog open). */
function stableInitialFiltersKey(initialFilters: Partial<SampleSearchCriteria> | undefined): string {
  if (initialFilters == null || Object.keys(initialFilters).length === 0) {
    return '';
  }
  const sortedKeys = Object.keys(initialFilters).sort() as (keyof SampleSearchCriteria)[];
  const normalized: Record<string, unknown> = {};
  for (const key of sortedKeys) {
    const v = initialFilters[key];
    if (v instanceof Date) {
      normalized[key as string] = v.toISOString();
    } else {
      normalized[key as string] = v;
    }
  }
  return JSON.stringify(normalized);
}

interface SampleSelectionDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Title of the dialog */
  title?: string;
  /** Callback when a sample is selected (single selection) */
  onSampleSelect?: (sample: SampleRowType) => void;
  /** Callback when the dialog is closed */
  onClose: () => void;
  /**
   * When true (default): checkbox column + "Use Selection" for many samples.
   * When false: no checkboxes; clicking one row selects it and closes the dialog.
   */
  allowMultiple?: boolean;
  /** Callback when multiple samples are selected (only used if allowMultiple is true) */
  onSamplesSelect?: (samples: SampleRowType[]) => void;
  /** Sample IDs already in the data package — their rows will be disabled */
  disabledSampleIds?: number[];
  /** Starting filter criteria when the dialog opens; user can change them. */
  initialFilters?: Partial<SampleSearchCriteria>;
}

const OPTION_KEYS: OptionKey[] = [
  'synthesisTechniques',
  'materials',
  'status',
  'activityType',
  'staff',
  'sampleReportingCategory',
];

export const SampleSelectionDialog: FC<SampleSelectionDialogProps> = ({
  open,
  title = 'Select Samples',
  onSampleSelect,
  onClose,
  allowMultiple = true,
  onSamplesSelect,
  disabledSampleIds,
  initialFilters,
}) => {
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);
  const persistScopeKey = useSampleGridPersistScopeKey();
  const setFilters = useSampleFilterStore((s) => s.setFilters);
  const setPaginationModel = useSampleFilterStore((s) => s.setPaginationModel);
  const paginationModel = useSampleFilterStore((s) => s.paginationModel);

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({ ids: new Set(), type: 'include' });
  const [selectedSamples, setSelectedSamples] = useState<SampleRowType[]>([]);

  const searchPanelKey = useMemo(() => stableInitialFiltersKey(initialFilters), [initialFilters]);

  /** Seed criteria for the panel (copy so local state never aliases the store). */
  const mergedPanelFilters = useMemo((): SampleSearchCriteria | undefined => {
    if (!open) return undefined;
    if (initialFilters != null && Object.keys(initialFilters).length > 0) {
      return { ...defaultFilterCriteria, ...initialFilters } as SampleSearchCriteria;
    }
    return { ...useSampleFilterStore.getState().filters };
  }, [open, initialFilters]);

  const handleControlledFiltersChange = useCallback(
    (criteria: SampleSearchCriteria, sourceFlags?: FilterSourceFlags) => {
      const clean = { ...criteria };
      delete clean.researchGroup;
      setFilters(clean, sourceFlags);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    },
    [setFilters, setPaginationModel]
  );

  const handleClose = () => {
    setSelectionModel({ ids: new Set(), type: 'include' });
    setSelectedSamples([]);
    onClose();
  };

  const handleSelectionChange = (newSelectionModel: GridRowSelectionModel) => {
    setSelectionModel(newSelectionModel);
  };

  const handleSampleSelect = (sample: SampleRowType) => {
    if (allowMultiple) {
      // In multiple mode, we don't auto-select, just update the selection model
      // The user will click "Use Selection" to confirm
    } else {
      // In single mode, auto-select and close
      onSampleSelect?.(sample);
      handleClose();
    }
  };

  const handleSamplesSelect = (samples: SampleRowType[]) => {
    // Store the selected samples for use when "Use Selection" is clicked
    setSelectedSamples(samples);
  };

  const handleUseSelection = () => {
    if (allowMultiple) {
      // Multiple selection mode - use the current selection
      if (selectionModel.ids.size > 0 && selectedSamples.length > 0) {
        onSamplesSelect?.(selectedSamples);
        handleClose();
      }
    }
    // Single selection is handled by handleSampleSelect
  };

  const canUseSelection = allowMultiple
    ? selectionModel.ids.size >= 1 // Multiple selection: allow if at least 1 sample selected
    : false; // Single selection: no need for button since it auto-selects

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexShrink: 0,
          pr: 1,
        }}
      >
        <Typography component="span" variant="h6" sx={{ fontWeight: 600, minWidth: 0 }}>
          {title}
        </Typography>
        <IconButton
          aria-label="Close sample selection"
          onClick={handleClose}
          edge="end"
          size="small"
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 2,
          pt: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ flexShrink: 0, borderBottom: '1px solid #e0e0e0', pb: 2, mb: 2 }}>
          {open && mergedPanelFilters ? (
            <SampleSearchPanel
              key={searchPanelKey}
              title={title}
              hideTitleRow
              isSelectionMode={true}
              filters={mergedPanelFilters}
              onFiltersChange={handleControlledFiltersChange}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
            />
          ) : null}
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            '& > .MuiDataGrid-root': {
              height: '100%',
            },
          }}
        >
          <SampleGridCore
            key={persistScopeKey}
            showLinks={false}
            enableSelection={true}
            selectionModel={selectionModel}
            onSelectionChange={handleSelectionChange}
            // Checkboxes only in multi-select mode; single-select uses row click only.
            checkboxSelection={allowMultiple}
            disableColumnMenu={true}
            showToolbar={false}
            density="compact"
            onSampleSelect={handleSampleSelect}
            onSamplesSelect={handleSamplesSelect}
            optionsData={options}
            optionsLoading={optionsLoading}
            disabledSampleIds={disabledSampleIds}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid #e0e0e0',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        <Button size="small" onClick={handleClose} color="inherit" sx={{ minWidth: 120 }}>
          Cancel
        </Button>
        {/* Single-select callers confirm by clicking a row; no "Use Selection" button. */}
        {allowMultiple ? (
          <Button
            size="small"
            onClick={handleUseSelection}
            variant="contained"
            disabled={!canUseSelection}
            sx={{ minWidth: 120 }}
          >
            Use Selection
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};
